import React from 'react';
import TableCard from '@/components/Tables/TableCard/TableCard';
import tableCardStyles from '@/components/Tables/TableCard/TableCard.module.css';
import { BundleDetails } from '@/types/bundles.types';
import { CellContext, Row } from '@tanstack/react-table';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import { useTranslation } from 'react-i18next';
import Link from '@/components/Link/Link';
import LinkIcon from '@mui/icons-material/Link';
import styles from '@/pages/Franchisee/Bundles/BundleLibrary/BundleLibrary.module.css';
import StarTooltip from '@/components/Icons/CustomIcons/StarTooltip';
import BundleLibraryTablePriceCell from '@/pages/Franchisee/Bundles/BundleLibrary/BundleLibraryTablePriceCell';

export interface IBundleLibraryTableProps {
  isLoading: boolean;
  bundleRecords: BundleDetails[];
  priceCellFunction?: (props: CellContext<BundleDetails, any>) => string;
}

const DisplayWithStarTooltip = ({
  id,
  bundleCode,
  isLinked,
}: {
  id: string;
  bundleCode: string;
  isLinked: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.starToolTipAndBundleCodeWrapper}>
      <StarTooltip content={t('bundleLibrary.nationalProgram')} className={styles.starSvg} position="top" />
      <div className={styles.bundleCodeAndLinkIconWrapper}>
        <Link to={`/bundles/${id}`}>{bundleCode}</Link>
        {isLinked && <LinkIcon style={{ fontSize: '20px', color: '#266DCC' }} />}
      </div>
    </div>
  );
};

const ViewBundle = ({ row }: CellContext<BundleDetails, unknown>) =>
  row.original.isNationalProgramLiveOrOptIn ? (
    <DisplayWithStarTooltip
      id={row.original.id}
      bundleCode={row.original.code}
      isLinked={!!row.original.linkedBundleName}
    />
  ) : (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Link to={`/bundles/${row.original.id}`}>{row.original.code}</Link>
      {row.original.linkedBundleName && <LinkIcon style={{ fontSize: '20px', color: '#266DCC' }} />}
    </div>
  );

const BundleLibraryTable: React.FC<IBundleLibraryTableProps> = ({ isLoading, bundleRecords, priceCellFunction }) => {
  const { t } = useTranslation();

  const PriceCell = priceCellFunction ?? BundleLibraryTablePriceCell;

  return (
    <TableCard
      className={tableCardStyles.cardWidth}
      title={t('bundleLibrary.table.title')}
      isLoading={isLoading}
      includeMinWidth={false}
      data={bundleRecords}
      columns={[
        {
          accessorFn: (originalRow: BundleDetails) => originalRow.code || '-',
          header: t('bundleLibrary.table.bundleCode'),
          enableSorting: true,
          cell: ViewBundle,
        },
        {
          accessorFn: (originalRow: BundleDetails) => originalRow.name || '-',
          header: t('bundleLibrary.table.displayName'),
          enableSorting: true,
        },
        {
          accessorFn: (originalRow: BundleDetails) => originalRow.internalName || '-',
          header: t('bundleLibrary.table.bundleDetail'),
          enableSorting: true,
        },
        {
          accessorKey: 'nationalPrices',
          header: t('bundleLibrary.table.priceRange'),
          cell: PriceCell,
          filterFn: (row: Row<BundleDetails>, columnId: string, filterValue) => {
            const availPrices = row.original.availablePrices;
            if (availPrices && availPrices.toString().includes(filterValue)) return true;
            return false;
          },
          enableSorting: true,
        },
        {
          accessorFn: (originalRow: BundleDetails) => originalRow.activeStoreGroupCount || '-',
          header: t('bundleLibrary.table.activeStoreGroups'),
          enableSorting: true,
        },
        {
          accessorKey: 'occasions',
          header: t('bundleLibrary.table.occasions'),
          cell: (props) => {
            const { occasions } = props.row.original;
            return renderOccasions(t, occasions);
          },
          enableSorting: true,
          meta: { disableFilter: true },
        },
        {
          accessorKey: 'bundleStatus',
          header: t('bundleLibrary.table.status'),
          cell: (props) => (props.row.original.bundleStatus ? 'Active' : 'Inactive'),
          enableSorting: true,
          meta: { disableFilter: true },
        },
      ]}
      returnedRecordsText={(count) => t('bundleLibrary.table.returnedRecord', { count })}
      enableFilters
      bundleLibraryCaption
    />
  );
};

export default BundleLibraryTable;
